import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { Package, Plus, Pencil, Trash2, Loader, AlertCircle } from 'lucide-react';
import {
  BuildModpackLink,
  CreateBuildModpackLinkRequest,
  UpdateBuildModpackLinkRequest,
} from '../../types/buildTeam';
import { buildTeamApiService } from '../../services/buildTeamApi';
import { CreatorProfile } from '../CreatorProfile';
import { usernameCacheService } from '../../services/usernameCache';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { ApogeePackEnrichment } from '../apogee/ApogeePackEnrichment';
import { ApogeePackSearchPicker } from './ApogeePackSearchPicker';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';
import type { PackRow } from '../../types/apogee';

export interface BuildModpacksSectionProps {
  targetId: string;
  targetType: 'project' | 'build';
  canEdit: boolean;
  canDelete: boolean;
  onUpdate: () => void;
}

const LABEL_CLASS = 'text-xs font-medium text-glass-400 mb-1.5 block';

export function BuildModpacksSection({
  targetId,
  targetType,
  canEdit,
  canDelete,
  onUpdate,
}: BuildModpacksSectionProps) {
  const [modpacks, setModpacks] = useState<BuildModpackLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const loadModpacks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data =
        targetType === 'project'
          ? await buildTeamApiService.getProjectModpacks(targetId)
          : await buildTeamApiService.getBuildModpacks(targetId);
      setModpacks(data);
      const linkedByIds = data.map((m) => m.linked_by).filter(Boolean);
      usernameCacheService.prefetchUsernames(linkedByIds);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load modpack links');
    } finally {
      setLoading(false);
    }
  }, [targetId, targetType]);

  useEffect(() => {
    loadModpacks();
  }, [loadModpacks]);

  const handleCreate = async (e: React.FormEvent, formData: CreateBuildModpackLinkRequest) => {
    e.preventDefault();
    if (!canEdit || submitting) return;
    setSubmitting(true);
    try {
      if (targetType === 'project') {
        await buildTeamApiService.linkModpackToProject(targetId, formData);
      } else {
        await buildTeamApiService.linkModpackToBuild(targetId, formData);
      }
      setShowCreate(false);
      await loadModpacks();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to link modpack');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (linkId: string, data: UpdateBuildModpackLinkRequest) => {
    if (!canEdit || submitting) return;
    setSubmitting(true);
    try {
      await buildTeamApiService.updateModpackLink(linkId, data);
      setEditingId(null);
      await loadModpacks();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update modpack link');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (linkId: string) => {
    if (!canDelete || deletingId) return;
    setConfirmDeleteId(linkId);
  };

  const executeDelete = async () => {
    if (!confirmDeleteId) return;
    setDeletingId(confirmDeleteId);
    setConfirmDeleteId(null);
    try {
      await buildTeamApiService.deleteModpackLink(confirmDeleteId);
      await loadModpacks();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove modpack link');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <SkeletonLoader className="h-4 w-28" />
          <SkeletonLoader className="h-9 w-32" />
        </div>
        <div className="divide-y divide-glass-200/10">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="py-3 first:pt-0 space-y-2">
              <SkeletonLoader className="h-4 w-48" />
              <SkeletonLoader className="h-3 w-64" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        bare
        compact
        tone="danger"
        icon={AlertCircle}
        title="Failed to load modpacks"
        description={error}
        action={
          <button type="button" onClick={loadModpacks} className="glass-button">
            Retry
          </button>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Modpacks"
        icon={Package}
        count={modpacks.length}
        className="mb-0"
        actions={
          canEdit ? (
            <button
              type="button"
              onClick={() => setShowCreate(!showCreate)}
              className="glass-button"
            >
              <Plus className="w-4 h-4" />
              Add modpack
            </button>
          ) : undefined
        }
      />

      {showCreate && canEdit && (
        <ModpackCreateForm
          onSubmit={handleCreate}
          onCancel={() => setShowCreate(false)}
          submitting={submitting}
        />
      )}

      {modpacks.length === 0 ? (
        <EmptyState
          bare
          compact
          icon={Package}
          title="No modpack links yet"
          description={canEdit ? 'Link an Apogee pack to this record.' : 'Linked modpacks will appear here.'}
        />
      ) : (
        <ul className="divide-y divide-glass-200/10">
          {modpacks.map((m) => (
            <li
              key={m.id}
              className="py-3 first:pt-0 last:pb-0 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between"
            >
              {editingId === m.id && canEdit ? (
                <ModpackInlineEdit
                  link={m}
                  onSave={(data) => handleUpdate(m.id, data)}
                  onCancel={() => setEditingId(null)}
                  submitting={submitting}
                />
              ) : (
                <>
                  <div className="min-w-0 space-y-1 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Package className="w-4 h-4 text-accent-300 shrink-0" />
                      <span className="text-sm font-medium text-white">{m.modpack_name}</span>
                      {m.modpack_version && (
                        <span className="badge badge-neutral font-mono">
                          v{m.modpack_version}
                        </span>
                      )}
                    </div>
                    {m.notes && <p className="text-xs text-glass-400">{m.notes}</p>}
                    <ApogeePackEnrichment
                      packName={m.modpack_name}
                      apogeeId={m.apogee_id ?? undefined}
                      mode="inline"
                    />
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-glass-500">
                      <span className="tabular-nums">
                        Linked{' '}
                        <time dateTime={new Date(m.linked_at).toISOString()}>
                          {format(new Date(m.linked_at), 'PPp')}
                        </time>
                      </span>
                      <span className="flex items-center gap-1">
                        by <CreatorProfile creatorId={m.linked_by} inline />
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => setEditingId(m.id)}
                        className="btn-icon touch-manipulation"
                        title="Edit version & notes"
                        aria-label="Edit version and notes"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => handleDelete(m.id)}
                        disabled={deletingId === m.id}
                        className="btn-icon text-red-400 hover:text-red-300 touch-manipulation"
                        title="Delete link"
                        aria-label="Delete link"
                      >
                        {deletingId === m.id ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {confirmDeleteId && (
        <ConfirmationModal
          isOpen
          onClose={() => setConfirmDeleteId(null)}
          onConfirm={executeDelete}
          title="Remove Modpack Link"
          message="Are you sure you want to remove this modpack link?"
          confirmText="Remove"
          isLoading={!!deletingId}
        />
      )}
    </div>
  );
}

function ModpackCreateForm({
  onSubmit,
  onCancel,
  submitting,
}: {
  onSubmit: (e: React.FormEvent, data: CreateBuildModpackLinkRequest) => void;
  onCancel: () => void;
  submitting: boolean;
}) {
  const [selectedPack, setSelectedPack] = useState<PackRow | null>(null);
  const [modpackVersion, setModpackVersion] = useState('');
  const [notes, setNotes] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPack) return;
    onSubmit(e, {
      modpack_name: selectedPack.name,
      apogee_id: selectedPack.apogee_id,
      modpack_version: modpackVersion.trim() || undefined,
      notes: notes.trim() || undefined,
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="subcard p-4 space-y-3"
    >
      <ApogeePackSearchPicker selected={selectedPack} onSelect={setSelectedPack} disabled={submitting} />
      <div>
        <label className={LABEL_CLASS}>Version</label>
        <input
          type="text"
          value={modpackVersion}
          onChange={(e) => setModpackVersion(e.target.value)}
          className="glass-input w-full"
          placeholder="Optional"
        />
      </div>
      <div>
        <label className={LABEL_CLASS}>Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="glass-input w-full resize-none"
          placeholder="Optional"
        />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="glass-button-secondary">
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting || !selectedPack}
          className="btn-primary"
        >
          {submitting ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Add link
        </button>
      </div>
    </form>
  );
}

function ModpackInlineEdit({
  link,
  onSave,
  onCancel,
  submitting,
}: {
  link: BuildModpackLink;
  onSave: (data: UpdateBuildModpackLinkRequest) => void;
  onCancel: () => void;
  submitting: boolean;
}) {
  const [modpackVersion, setModpackVersion] = useState(link.modpack_version || '');
  const [notes, setNotes] = useState(link.notes || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      modpack_version: modpackVersion.trim() || null,
      notes: notes.trim() || null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="w-full subcard p-4 space-y-3">
      <div className="flex items-center gap-2 text-sm text-glass-300">
        <Package className="w-4 h-4 text-accent-300" />
        <span className="font-medium text-white">{link.modpack_name}</span>
      </div>
      <div>
        <label className={LABEL_CLASS}>Version</label>
        <input
          type="text"
          value={modpackVersion}
          onChange={(e) => setModpackVersion(e.target.value)}
          className="glass-input w-full"
        />
      </div>
      <div>
        <label className={LABEL_CLASS}>Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="glass-input w-full resize-none"
        />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="glass-button-secondary">
          Cancel
        </button>
        <button type="submit" disabled={submitting} className="btn-primary">
          {submitting ? <Loader className="w-4 h-4 animate-spin" /> : 'Save'}
        </button>
      </div>
    </form>
  );
}
